/* prettier-ignore */

import { effect, inject, Injectable, NgZone, signal } from '@angular/core';
import { ScrollService } from './scrollContainer.service';
import { WebSocketService } from './webSocket.service';
import { DataChatService } from './dataChat.service';
import { ChatBotTextAnimationService } from './textAnimationService.service';
import { environment } from 'src/environments/environment';
import { PlayAudioHandlerService } from './playAudioHandler.service';
import { LocalFillerPhrasesService } from './localFillerPhrases.service';
import { RestService } from 'src/app/services/rest.service';
import { TranslationService } from 'src/app/services/translation.service';
import { v4 as uuidv4 } from 'uuid';

const TARGET_SR = 16000;
const CHANNELS = 1;
const MIN_VOICE_RMS = 0.008;
const MIN_DURATION_SEC = 0.75;
const FILLER_INTERVAL_MS = 7500;
const PCM_BATCH_SAMPLES = 1024;
const FALLBACK_PROCESSOR_SIZE = 2048;
const HIGHPASS_FREQUENCY_HZ = 100;
const WAVE_REDRAW_MS = 40;

@Injectable({ providedIn: 'root' })
export class RecordAudioHandlerService {
  private readonly ngZone = inject(NgZone);
  private readonly webSocketService = inject(WebSocketService);
  private readonly dataChat = inject(DataChatService);
  private readonly _translationService = inject(TranslationService);
  private readonly textAnimationService = inject(ChatBotTextAnimationService);
  private readonly playService = inject(PlayAudioHandlerService);
  private readonly localFillerService = inject(LocalFillerPhrasesService);
  private readonly rest = inject(RestService);
  public readonly scrollService = inject(ScrollService);

  protected readonly lang = this._translationService.selectedLanguage;
  private stream?: MediaStream;
  private audioCtx?: AudioContext;
  private workletNode?: AudioWorkletNode;
  private scriptProcessor?: ScriptProcessorNode;
  private fallbackSilentGain?: GainNode;
  private filterNode?: BiquadFilterNode;
  private source?: MediaStreamAudioSourceNode;
  private analyser?: AnalyserNode;

  private chunks: Int16Array[] = [];
  private pendingChunks: Int16Array[] = [];
  private pendingSamples = 0;
  private activeTracks = new Set<MediaStreamTrack>();
  private fillerInterval: ReturnType<typeof setInterval> | null = null;
  private fillerTimeout: ReturnType<typeof setTimeout> | null = null;

  private tearingDown = signal(false);
  private processingStop = signal(false);
  public isRecording = signal(false);
  public isLongPressMode = signal(false);

  private animFrameId = 0;
  private freqData?: Uint8Array;

  private onVisChange?: () => void;

  listenAbortEffect = effect((onCleanup) => {
    const controller = this.dataChat.abortSignal();
    const onAbort = () => {
        this.clearFillerInterval();
    };

    controller.signal.addEventListener('abort', onAbort);
    onCleanup(() => {
        controller.signal.removeEventListener('abort', onAbort);
    });
});

// ======================================== PUBLIC API ========================================
  async startRecording(wavesCanvas?: HTMLCanvasElement): Promise<void> {
    if (this.isRecording()) 
      return;

    this.isRecording.set(true);
    // HRS-3045 (bloque 4): la respuesta de voz llega como chunks por el WS; con la
    // conexión capability-driven puede no estar abierta aún (cinturón y tirantes:
    // el efecto por capability ya conecta cuando allowSendAudio se activa).
    this.webSocketService.ensureConnected();
    this.resetPcmBuffers();
    this.dataChat.abortSignal.set(new AbortController());
    this.playService.unlockAudioFromGesture();
    this.localFillerService.unlockAudioFromGesture();

    try {
      await this.webSocketService.cancelCompletion();
      this.clearFillerInterval();

      const userMessages = this.dataChat.messages()?.filter(m => m.role === 'user')?.length;
      if (!userMessages && !this.isLongPressMode()){
        await this.localFillerService.playWelcome();
        if (this.dataChat.abortSignal().signal?.aborted)
          return;

        await this.enableMic(wavesCanvas);
        return;
      }

      await this.enableMic(wavesCanvas);

    } catch (error) {
      console.error('[startRecording] Error:', error);
      await this.disableMic();
      throw error;
    }
  }

  async stopRecording(): Promise<void> {
    if (this.processingStop()) 
      return;
    
    this.processingStop.set(true);
    this.dataChat.abortSignal().abort();

    this.clearFillerInterval();
    await this.flushWorkletTail();
    this.flushPendingChunks();
    const capturedChunks = this.chunks.slice();
    await this.disableMic();

    try {
      const merged = this.mergeChunks(capturedChunks);
      const durationSec = merged.length / TARGET_SR;

      if (!this.hasVoice(merged) || durationSec < MIN_DURATION_SEC) {
        this.dataChat.isStreaming.set(false);
        this.dataChat.chatIsThinking.set(false);
        console.warn('[stopRecording] Audio silencioso o muy corto, descartado');
        return;
      }

      const wav = this.buildWav(merged);
      const blob = new Blob([wav], { type: 'audio/wav' });
      this.dataChat.isStreaming.set(true);
      this.dataChat.chatIsThinking.set(true);
      await this.sendAudio(blob);
    } catch (err) {
      console.error('[stopRecording] Error:', err);
      this.dataChat.isStreaming.set(false);
      this.dataChat.chatIsThinking.set(false);
      throw err;
    } finally {
      this.processingStop.set(false);
      this.resetPcmBuffers();
    }
  }

// ======================================== MIC LIFECYCLE ========================================
  private async enableMic(wavesCanvas?: HTMLCanvasElement): Promise<void> {
    if (this.audioCtx) 
      await this.disableMic();

    if (this.dataChat.abortSignal().signal?.aborted) 
      return;

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: { ideal: CHANNELS, max: CHANNELS },
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false,
        sampleRate: { ideal: 48000 },
        sampleSize: { ideal: 16 },
      },
      video: false,
    });

    if (this.dataChat.abortSignal().signal?.aborted) {
      this.stopTracks(stream);
      return;
    }

    this.stream = stream;
    this.trackStream(stream);

    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    this.audioCtx = new Ctx({ latencyHint: 'interactive' });
    if (this.dataChat.abortSignal().signal?.aborted) { 
      await this.disableMic(); 
      return; 
    }

    this.buildAudioGraph();
    await this.audioCtx!.resume();
    if (this.dataChat.abortSignal().signal?.aborted) { 
      await this.disableMic(); 
      return; 
    }

    await this.connectProcessor(this.dataChat.abortSignal().signal);
    this.setupWaves(wavesCanvas);
    this.attachVisibilityHandler();
    // HRS-2398/B: do NOT play "listening" filler words ("ok/vale/ummmm") while the user
    // is recording — they were scheduled at 7.5s/15s/… and talked OVER the user, cutting
    // them off ("la muletilla emmm"). Each filler is also a front→back /tts/simple call.
    // Removed so the assistant lets the user finish speaking. (clearFillerInterval stays
    // wired elsewhere as a harmless no-op.)
    // this.scheduleFillerPhrases();
  }

  private buildAudioGraph(): void {
    const ctx = this.audioCtx!;

    this.filterNode = ctx.createBiquadFilter();
    this.filterNode.type = 'highpass';
    this.filterNode.frequency.value = HIGHPASS_FREQUENCY_HZ;
    this.filterNode.Q.value = 0.707;

    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyser.smoothingTimeConstant = 0.75;

    this.source = ctx.createMediaStreamSource(this.stream!);
    this.source.connect(this.filterNode);
    this.filterNode.connect(this.analyser);
  }

  private async connectProcessor(signal: AbortSignal): Promise<void> {
    if (!this.audioCtx || !this.filterNode) 
      return;

    if ('audioWorklet' in this.audioCtx) {
      try {
        await this.audioCtx.audioWorklet.addModule('assets/audio-worklet/pcm-worklet.js');
        if (signal.aborted) { 
          await this.disableMic(); 
          return; 
        }

        this.workletNode = new AudioWorkletNode(this.audioCtx, 'pcm-worklet', {
          numberOfInputs: 1,
          numberOfOutputs: 0,
          processorOptions: { chunkSize: PCM_BATCH_SAMPLES, targetSampleRate: TARGET_SR },
        });
        this.workletNode.port.postMessage({ type: 'reset' });
        this.filterNode.connect(this.workletNode);

        this.workletNode.port.onmessage = ({ data }: MessageEvent<ArrayBuffer>) => {
          if (!this.isRecording() || !(data instanceof ArrayBuffer)) 
            return;

          this.handleIncomingPcmChunk(new Int16Array(data));
        };
        return;
      } catch (e) {
        console.warn('[AudioWorklet] No disponible, usando ScriptProcessor fallback.', e);
      }
    }

    if (signal.aborted) { 
      await this.disableMic(); 
      return; 
    }

    this.setupScriptProcessorFallback();
  }

  private setupScriptProcessorFallback(): void {
    const ctx = this.audioCtx!;
    this.scriptProcessor = ctx.createScriptProcessor(FALLBACK_PROCESSOR_SIZE, CHANNELS, CHANNELS);
    this.filterNode!.connect(this.scriptProcessor);

    // ScriptProcessor requires a path to destination to keep callbacks alive.
    this.fallbackSilentGain = ctx.createGain();
    this.fallbackSilentGain.gain.value = 0;
    this.scriptProcessor.connect(this.fallbackSilentGain);
    this.fallbackSilentGain.connect(ctx.destination);

    const inputSR = ctx.sampleRate;

    this.scriptProcessor.onaudioprocess = (ev: AudioProcessingEvent) => {
      if (!this.isRecording() || !this.scriptProcessor) 
        return;

      const input = ev.inputBuffer.getChannelData(0);
      const downsampled = this.downsample(input, inputSR, TARGET_SR);
      this.handleIncomingPcmChunk(this.float32ToInt16(downsampled));
    };
  }

  async disableMic(): Promise<void> {
    if (this.tearingDown()) 
      return;

    this.isRecording.set(false);
    this.tearingDown.set(true);
    this.detachVisibilityHandler();
    this.stopWaves();

    try {
      this.disconnectNodes();

      this.activeTracks.forEach(t => { try { t.stop(); } catch { /* noop */ } });
      this.activeTracks.clear();

      try { await this.audioCtx?.suspend(); } catch { /* noop */ }
      try { await this.audioCtx?.close(); } catch { /* noop */ }
    } finally {
      this.audioCtx = undefined;
      this.workletNode = undefined;
      this.scriptProcessor = undefined;
      this.fallbackSilentGain = undefined;
      this.source = undefined;
      this.filterNode = undefined;
      this.stream = undefined;
      this.tearingDown.set(false);
    }
  }

// ======================================== AUDIO UTILITIES ========================================
  private hasVoice(pcm: Int16Array): boolean {
    const windowSize = Math.floor(0.02 * TARGET_SR);
    if (windowSize <= 0 || pcm.length < windowSize) 
      return false;

    let voiced = 0;
    let total = 0;

    for (let i = 0; i + windowSize <= pcm.length; i += windowSize) {
      let sum = 0;
      const end = i + windowSize;

      for (let j = i; j < end; j++) {
        const s = pcm[j] / 32768;
        sum += s * s;
      }

      const rms = Math.sqrt(sum / windowSize);
      if (rms > MIN_VOICE_RMS)
        voiced++;

      total++;
    }

    return total > 0 && (voiced / total) >= 0.1;
  }

  private float32ToInt16(input: Float32Array): Int16Array {
    const out = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return out;
  }

  private downsample(input: Float32Array, inSR: number, outSR: number): Float32Array {
    if (outSR >= inSR) 
      return input.slice();

    const ratio = inSR / outSR;
    const outLen = Math.floor(input.length / ratio);
    const output = new Float32Array(outLen);

    let idx = 0;
    for (let i = 0; i < outLen; i++) {
      const nextIdx = Math.round((i + 1) * ratio);
      let sum = 0, count = 0;
      while (idx < nextIdx && idx < input.length) {
        sum += input[idx++];
        count++;
      }
      output[i] = count ? sum / count : 0;
    }
    return output;
  }

  private mergeChunks(chunks: Int16Array[]): Int16Array {
    if (!chunks.length) 
      return new Int16Array(0);

    const total = chunks.reduce((s, c) => s + c.length, 0);
    const result = new Int16Array(total);
    let off = 0;
    for (const c of chunks) {
      result.set(c, off);
      off += c.length;
    }
    return result;
  }

  private handleIncomingPcmChunk(chunk: Int16Array): void {
    if (!chunk.length) 
      return;

    this.pendingChunks.push(chunk);
    this.pendingSamples += chunk.length;

    if (this.pendingSamples >= PCM_BATCH_SAMPLES) 
      this.flushPendingChunks();
  }

  private flushPendingChunks(): void {
    if (!this.pendingSamples) 
      return;

    const merged = this.mergeChunks(this.pendingChunks);
    if (merged.length) 
      this.chunks.push(merged);

    this.pendingChunks.length = 0;
    this.pendingSamples = 0;
  }

  private resetPcmBuffers(): void {
    this.chunks.length = 0;
    this.pendingChunks.length = 0;
    this.pendingSamples = 0;
  }

  private buildWav(pcm: Int16Array): ArrayBuffer {
    const dataBytes = pcm.length * 2;
    const buf = new ArrayBuffer(44 + dataBytes);
    const v = new DataView(buf);
    let o = 0;

    const str = (s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); o += s.length; };
    const u32 = (n: number) => { v.setUint32(o, n, true); o += 4; };
    const u16 = (n: number) => { v.setUint16(o, n, true); o += 2; };

    str('RIFF'); u32(36 + dataBytes); str('WAVE');
    str('fmt '); u32(16); u16(1); u16(CHANNELS);
    u32(TARGET_SR); u32(TARGET_SR * CHANNELS * 2); u16(CHANNELS * 2); u16(16);
    str('data'); u32(dataBytes);

    for (let i = 0; i < pcm.length; i++) {
      v.setInt16(o, pcm[i], true);
      o += 2;
    }
    return buf;
  }

// ======================================== HTTP ========================================
  private async sendAudio(blob: Blob): Promise<void> {
    // HRS-3940: sin chatbot no se envía la voz del visitante a ningún servicio.
    let serviceId: number;
    try {
      serviceId = this.dataChat.requireServiceId();
    } catch (error) {
      console.error('[sendAudio]', error);
      this.dataChat.isStreaming.set(false);
      this.dataChat.chatIsThinking.set(false);
      this.dataChat.showConnectionErrorMessage();
      return;
    }

    const completionId = uuidv4();
    this.webSocketService.trackCompletionId(completionId);

    const subdomain = this.rest.subDomain || '';
    // HRS-2948: idioma del chat (no del navegador), alineado con el ?language=
    // del /tts/stream de la respuesta; el fallback cubre el arranque, antes de
    // que cargue el fichero remoto de idioma.
    const language = this.lang();

    const form = new FormData();
    form.append('file', blob, 'audio.wav');
    form.append('user_id', this.dataChat.token() || 'anonymous');
    form.append('service', String(serviceId));
    form.append('is_first_voice_message', 'false');
    form.append('completion_id', completionId);
    if (subdomain) {
      form.append('subdomain', subdomain);
      form.append('app', subdomain);
    }
    form.append('location', '{}');
    form.append('language', language);
    form.append('route_active', 'false');

    // const routeToken = this.getRouteToken();
    // if (routeToken) 
    //   form.append('route_token', routeToken);
    // form.append('route_active', this.hasActiveRoute() ? 'true' : 'false');

    try {
      const res = await fetch(`${environment.apiAssistantUrlBase}/api/chat/audio`, {
        method: 'POST',
        body: form,
      });

      if (!res.ok) 
        throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      if (data.transcription?.trim()) 
        this.textAnimationService.createUserMessage(data.transcription);

      if (data.message_id) {
        this.webSocketService.trackCompletionId(data.message_id, 'text');
        const ttsUrl = `${environment.apiAssistantUrlBase}/tts/stream/${data.message_id}?language=${encodeURIComponent(language)}`;
        this.localFillerService.playTypingSound();
        this.playService.playExternalAudio(ttsUrl);
      }
    } catch (error) {
      console.error('[sendAudio] Error:', error);
      this.dataChat.isStreaming.set(false);
      this.dataChat.chatIsThinking.set(false);
      throw error;
    }
  }

// ======================================== WAVES VISUALIZATION ========================================
  private setupWaves(canvas?: HTMLCanvasElement): void {
    if (!canvas || !this.analyser) 
      return;

    this.freqData = new Uint8Array(this.analyser.frequencyBinCount);
    this.drawWaves(canvas);
  }

  private drawWaves(canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) 
      return;

    const barSpacing = 6;
    const minHeight = 4;
    let lastTs = 0;

    this.ngZone.runOutsideAngular(() => {
      const loop = (ts = 0) => {
        this.animFrameId = requestAnimationFrame(loop);
        if (ts - lastTs < WAVE_REDRAW_MS || document.visibilityState !== 'visible') 
          return;

        lastTs = ts;
        if (!this.analyser || !this.freqData) 
          return;

        this.analyser.getByteFrequencyData(this.freqData ! as any);
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const totalBars = Math.floor(canvas.width / (barSpacing + 1));
        const cx = canvas.width / 2;
        const cy = canvas.height / 2;
        const half = Math.floor(totalBars / 2);
        const color = this.dataChat.chatStyles.bot_border_color || '#666';
        ctx.fillStyle = color;

        for (let i = 0; i < half; i++) {
          const idx = Math.floor(i * (this.freqData!.length / half));
          const h = Math.max(minHeight, (this.freqData![idx] / 255) * (canvas.height / 2));
          ctx.fillRect(cx - i * (barSpacing + 1), cy - h, 2, h * 2);
          ctx.fillRect(cx + i * (barSpacing + 1), cy - h, 2, h * 2);
        }
      };
      this.animFrameId = requestAnimationFrame(loop);
    });
  }

  private stopWaves(): void {
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = 0;
    }
    try { this.analyser?.disconnect(); } catch { /* noop */ }
    this.analyser = undefined;
    this.freqData = undefined;
  }

 // ======================================== HELPERS ========================================
  private scheduleFillerPhrases(): void {
    this.clearFillerInterval();

    this.fillerTimeout = setTimeout(() => {
      this.fillerTimeout = null;

      if (!this.isRecording()) return;

      this.localFillerService.sayListeningFiller();

      this.fillerInterval = setInterval(() => {
        if (this.isRecording()) {
          this.localFillerService.sayListeningFiller();
        }
      }, FILLER_INTERVAL_MS);
    }, FILLER_INTERVAL_MS);
  }

  private clearFillerInterval(): void {
    if (this.fillerTimeout) {
      clearTimeout(this.fillerTimeout);
      this.fillerTimeout = null;
    }

    if (this.fillerInterval) {
      clearInterval(this.fillerInterval);
      this.fillerInterval = null;
    }
  }

  private trackStream(stream: MediaStream): void {
    stream.getTracks().forEach(t => this.activeTracks.add(t));
  }

  private stopTracks(stream: MediaStream): void {
    stream.getTracks().forEach(t => t.stop());
  }

  private disconnectNodes(): void {
    if (this.workletNode?.port) {
      this.workletNode.port.onmessage = null;
      try { this.workletNode.port.close(); } catch { /* noop */ }
    }
    const nodes = [this.workletNode, this.scriptProcessor, this.filterNode, this.source, this.analyser, this.fallbackSilentGain];
    for (const node of nodes) {
      try { node?.disconnect(); } catch { /* noop */ }
    }
  }

  private async flushWorkletTail(): Promise<void> {
    if (!this.workletNode?.port) 
      return;

    try {
      this.workletNode.port.postMessage({ type: 'flush' });
      await this.wait(20);
    } catch { /* noop */ }
  }

  private wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private attachVisibilityHandler(): void {
    this.detachVisibilityHandler();
    this.onVisChange = async () => {
      if (this.audioCtx && document.visibilityState === 'visible' && this.audioCtx.state === 'suspended') {
        try { await this.audioCtx.resume(); } catch { /* noop */ }
      }
    };
    document.addEventListener('visibilitychange', this.onVisChange, { passive: true });
  }

  private detachVisibilityHandler(): void {
    if (this.onVisChange) {
      document.removeEventListener('visibilitychange', this.onVisChange);
      this.onVisChange = undefined;
    }
  }

}
